/**
 * News query hooks for news articles and sentiment
 *
 * Provides React Query hooks for Phase 9 (News & Sentiment) data
 */
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import {
  fetchNewsForSymbol,
  fetchNewsSummary,
  fetchMaterialNews,
  fetchNewsFeed,
  fetchNewsArticle,
  fetchSymbolsWithNews,
  type FetchNewsForSymbolParams,
  type FetchNewsFeedParams,
  type FetchMaterialNewsParams,
} from '../api/news'
import type {
  NewsListResponse,
  NewsSummaryResponse,
  MaterialNewsResponse,
  NewsFeedResponse,
  NewsArticleWithSentiment,
  SymbolsWithNewsResponse,
} from '../types/news'

// =============================================================================
// Query Keys
// =============================================================================

export const NEWS_KEYS = {
  all: ['news'] as const,
  forSymbol: (symbol: string, params?: FetchNewsForSymbolParams) =>
    ['news', 'symbol', symbol, params] as const,
  summary: (symbol: string, days?: number) =>
    ['news', 'summary', symbol, days] as const,
  material: (params?: FetchMaterialNewsParams) =>
    ['news', 'material', params] as const,
  feed: (params?: FetchNewsFeedParams) =>
    ['news', 'feed', params] as const,
  article: (articleId: number) =>
    ['news', 'article', articleId] as const,
  symbols: () =>
    ['news', 'symbols'] as const,
}

// =============================================================================
// Symbol-specific Hooks
// =============================================================================

/**
 * Fetch news articles for a specific symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param params Optional filters (days, limit, offset, category, sentiment, materialOnly)
 */
export function useNewsForSymbol(
  symbol: string | undefined,
  params?: FetchNewsForSymbolParams
) {
  return useQuery<NewsListResponse>({
    queryKey: NEWS_KEYS.forSymbol(symbol ?? '', params),
    queryFn: () => fetchNewsForSymbol(symbol!, params),
    enabled: !!symbol,
  })
}

/**
 * Fetch news summary statistics for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param days Lookback period in days (default 30)
 */
export function useNewsSummary(symbol: string | undefined, days?: number) {
  return useQuery<NewsSummaryResponse>({
    queryKey: NEWS_KEYS.summary(symbol ?? '', days),
    queryFn: () => fetchNewsSummary(symbol!, days),
    enabled: !!symbol,
  })
}

// =============================================================================
// Material News Hooks
// =============================================================================

/**
 * Fetch material news across multiple symbols
 * @param params Optional filters (symbols, days, limit)
 * @param enabled Whether to enable the query (default true)
 */
export function useMaterialNews(
  params?: FetchMaterialNewsParams,
  enabled: boolean = true
) {
  return useQuery<MaterialNewsResponse>({
    queryKey: NEWS_KEYS.material(params),
    queryFn: () => fetchMaterialNews(params),
    enabled,
  })
}

// =============================================================================
// News Feed Hooks
// =============================================================================

/**
 * Fetch combined news feed with pagination
 * @param params Optional filters (symbols, categories, days, limit, offset, sentiment, materialOnly)
 * @param enabled Whether to enable the query (default true)
 */
export function useNewsFeed(
  params?: FetchNewsFeedParams,
  enabled: boolean = true
) {
  return useQuery<NewsFeedResponse>({
    queryKey: NEWS_KEYS.feed(params),
    queryFn: () => fetchNewsFeed(params),
    enabled,
  })
}

/**
 * Infinite scroll news feed - loads more articles on demand
 * @param params Optional filters (symbols, categories, days, sentiment, materialOnly)
 * @param pageSize Number of articles per page (default 50)
 */
export function useInfiniteNewsFeed(
  params?: Omit<FetchNewsFeedParams, 'offset' | 'limit'>,
  pageSize: number = 50
) {
  return useInfiniteQuery<NewsFeedResponse>({
    queryKey: [...NEWS_KEYS.feed(params), 'infinite'] as const,
    queryFn: async ({ pageParam = 0 }) => {
      return fetchNewsFeed({
        ...params,
        limit: pageSize,
        offset: pageParam as number,
      })
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((acc, page) => acc + page.articles.length, 0)
      if (totalLoaded >= lastPage.total) {
        return undefined // No more pages
      }
      return totalLoaded
    },
  })
}

// =============================================================================
// Article Detail Hooks
// =============================================================================

/**
 * Fetch detailed news article by ID
 * @param articleId Database ID of the article (undefined disables query)
 */
export function useNewsArticle(articleId: number | undefined) {
  return useQuery<NewsArticleWithSentiment>({
    queryKey: NEWS_KEYS.article(articleId ?? 0),
    queryFn: () => fetchNewsArticle(articleId!),
    enabled: articleId !== undefined && articleId > 0,
  })
}

// =============================================================================
// Symbol List Hooks
// =============================================================================

/**
 * Fetch list of symbols with available news data
 */
export function useSymbolsWithNews() {
  return useQuery<SymbolsWithNewsResponse>({
    queryKey: NEWS_KEYS.symbols(),
    queryFn: fetchSymbolsWithNews,
    staleTime: 5 * 60 * 1000, // 5 minutes - symbols don't change often
  })
}
